import { useEffect, useState, useCallback } from "react";
import { Purchases, LOG_LEVEL } from "@revenuecat/purchases-capacitor";

// ─── CONFIG ────────────────────────────────────────────────────────────────
// Paste your RevenueCat API keys here (from RevenueCat dashboard → API Keys)
const RC_ANDROID_KEY = "goog_FjcrNVYwpoSrgSeLLOkMpJrvZPZ";
const RC_IOS_KEY     = "appl_AaOzxHXSIxknzLcXIsCpysEelpr";

// The entitlement identifier you'll create in RevenueCat dashboard
const ENTITLEMENT_PRO = "pro";

// Product identifiers — must match what you create in Play Console & App Store Connect
// iOS uses v4 IDs (fresh products to escape the Developer Action Needed loop).
// Android keeps the existing working IDs.
const IOS_PRODUCT_IDS = {
  monthly: "elde_pro_monthly_v4",
  annual:  "elde_pro_yearly_v4",
};
const ANDROID_PRODUCT_IDS = {
  monthly: "elde_pro_monthly:monthly",
  annual:  "elde_pro_yearly:yearly",
};

function getProductIds() {
  const platform = window.Capacitor?.getPlatform?.() || "android";
  return platform === "ios" ? IOS_PRODUCT_IDS : ANDROID_PRODUCT_IDS;
}

// Backwards-compatible export (defaults to Android IDs for any non-native callers)
export const PRODUCT_IDS = ANDROID_PRODUCT_IDS;
// ───────────────────────────────────────────────────────────────────────────

let _initialized = false;

/**
 * Initialize RevenueCat. Call once on app start (in App.js useEffect).
 * Pass the Supabase user ID so RevenueCat links purchases to your user.
 */
export async function initRevenueCat(userId) {
  try {
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    if (!isNative) {
      // RevenueCat Capacitor SDK only works on native — skip in browser
      console.log("[RC] Skipping init — not a native platform");
      return;
    }

    if (_initialized) {
      // Already configured — just identify the user
      if (userId) await Purchases.logIn({ appUserID: userId });
      return;
    }

    const platform = window.Capacitor?.getPlatform?.() || "android";
    const apiKey = platform === "ios" ? RC_IOS_KEY : RC_ANDROID_KEY;

    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    await Purchases.configure({ apiKey });
    _initialized = true;

    if (userId) {
      await Purchases.logIn({ appUserID: userId });
    }

    console.log("[RC] Initialized successfully");
  } catch (err) {
    console.error("[RC] Init error:", err);
  }
}

/**
 * Hook — provides isPro status, purchase function, and restore function.
 * 
 * Usage in App.js:
 *   const { isPro: rcIsPro, purchasePlan, restorePurchases, rcReady } = useRevenueCat(session?.user?.id);
 */
export function useRevenueCat(userId) {
  const [rcIsPro, setRcIsPro]   = useState(false);
  const [rcReady, setRcReady]   = useState(false);
  const [rcLoading, setRcLoading] = useState(false);

  const isNative = !!(window.Capacitor?.isNativePlatform?.());

  // Check entitlement status
  const checkProStatus = useCallback(async () => {
    if (!isNative) return false;
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      const active = customerInfo.entitlements.active;
      const hasPro = !!active[ENTITLEMENT_PRO];
      setRcIsPro(hasPro);
      return hasPro;
    } catch (err) {
      console.error("[RC] checkProStatus error:", err);
      return false;
    }
  }, [isNative]);

  // Init + identify on mount / userId change
  useEffect(() => {
    if (!userId) return;
    (async () => {
      await initRevenueCat(userId);
      await checkProStatus();
      setRcReady(true);
    })();
  }, [userId, checkProStatus]);

  /**
   * Purchase a plan.
   * @param {"monthly"|"annual"|"yearly"} plan
   * @returns {{ success: boolean, error?: string }}
   */
  const purchasePlan = useCallback(async (plan = "monthly") => {
    if (!isNative) {
      // On web, fall through to Stripe (caller handles this)
      return { success: false, error: "not_native" };
    }
    setRcLoading(true);
    try {
      // Normalize plan key — the paywall (ProGate) uses "yearly", the product
      // maps use "annual". Treat them as the same so neither falls through to
      // the monthly product by accident.
      const planKey = plan === "yearly" ? "annual" : plan;
      const ids = getProductIds();
      const productId = ids[planKey] ?? ids.monthly;

      // Get available packages. NOTE: getOfferings() returns the offerings
      // object DIRECTLY ({ all, current }) — unlike getCustomerInfo/purchasePackage,
      // it is NOT wrapped in { offerings }. Destructuring `.offerings` would yield
      // undefined and falsely throw "No offerings available".
      let offerings = await Purchases.getOfferings();
      if (typeof offerings === "string") {
        try { offerings = JSON.parse(offerings); } catch { /* leave as-is */ }
      }

      // Prefer the "current" offering, but fall back to the "default" offering
      // (or the first one) — on iOS the SDK sometimes returns offerings under
      // `all` without populating `current`.
      const current =
        offerings?.current ||
        offerings?.all?.default ||
        (offerings?.all ? Object.values(offerings.all)[0] : null);
      if (!current) throw new Error("No offerings available");

      // Find the matching package by product identifier
      const pkg = current.availablePackages.find(
        (p) => p.product.identifier === productId
      );
      if (!pkg) throw new Error(`Package not found: ${productId}`);

      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      const hasPro = !!customerInfo.entitlements.active[ENTITLEMENT_PRO];
      setRcIsPro(hasPro);
      return { success: hasPro };
    } catch (err) {
      // User cancelled — not a real error
      if (err?.code === "1" || err?.message?.includes("cancel")) {
        return { success: false, error: "cancelled" };
      }
      console.error("[RC] purchasePlan error:", err);
      return { success: false, error: err?.message || "unknown_error" };
    } finally {
      setRcLoading(false);
    }
  }, [isNative]);

  /**
   * Restore previous purchases (required by App Store / Play Store policies).
   * @returns {{ success: boolean, isPro: boolean }}
   */
  const restorePurchases = useCallback(async () => {
    if (!isNative) return { success: false, isPro: false };
    setRcLoading(true);
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const hasPro = !!customerInfo.entitlements.active[ENTITLEMENT_PRO];
      setRcIsPro(hasPro);
      return { success: true, isPro: hasPro };
    } catch (err) {
      console.error("[RC] restorePurchases error:", err);
      return { success: false, isPro: false };
    } finally {
      setRcLoading(false);
    }
  }, [isNative]);

  return { rcIsPro, rcReady, rcLoading, purchasePlan, restorePurchases, checkProStatus };
}
